import { theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarScrollOverEffect, useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { GlobalChecker } from "~/components/article/article-render/global-checker";
import { useMobileInlineAdSlotIsDisabledFlag } from "~/components/article/article-render/hooks";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import type { infographicArticleArticle$key } from "~/queries/__generated__/infographicArticleArticle.graphql";

import {
  AppBarScrollOverTracker,
  AuthorDetail,
  CommentAndReaction,
  Content,
  FooterAdSlot,
  HeaderAdSlot,
  InfographicArticleContainer,
  InfographicArticleWrapper,
  LandingBanner,
  Left,
  Social,
  SocialDesktop,
  SocialMobile,
  SpeechWrapper,
  SummaryAndAuthor,
  Topic,
} from "./styles";

export type Props = ArticleRenderChildProps & {
  reference: infographicArticleArticle$key;
};

export const InfographicArticle: FunctionComponent<Props> = ({
  provider,
  reference: reference_,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const article = useFragment(
    graphql`
      fragment infographicArticleArticle on Article {
        ...landingBannerInfographicArticle
        ...globalCheckerContent
        ...blockquoteLongReadContent
      }
    `,
    reference_,
  );

  const { trackerNode } = useAppBarScrollOverEffect({
    swapTiming: "exit",
    swapVariant: "scmp/generic-light",
    tracker: AppBarScrollOverTracker,
  });

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();

  return (
    <InfographicArticleContainer>
      <InfographicArticleWrapper>
        <LandingBanner provider={provider} reference={article} />
        <SummaryAndAuthor>
          {provider.subHeadline()}
          {provider.authorBlock()}

          <SpeechWrapper>
            {provider.speechPlayer({})} {provider.speechPlayerPortal({})}
          </SpeechWrapper>
        </SummaryAndAuthor>

        {trackerNode}

        <Content>
          {provider.body({
            extraComponentMap: {
              "inline-widget": null,
            },
            inlineAdSlots: {
              "1": {
                adUnit: "m_lrec1",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "2": {
                adUnit: "d_lrec2",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "3": {
                adUnit: "m_lrec4d",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "4": {
                adUnit: "m_lrec4e",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "5": {
                adUnit: "m_lrec4f",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
            },
          })}
        </Content>
        <CommentAndReaction>{provider.commentTriggerAndReaction()}</CommentAndReaction>
        <Social>
          <SocialMobile>
            {provider.shareWidget({
              variant: "longReadMobile",
            })}
          </SocialMobile>
          <SocialDesktop>
            {provider.shareWidget({
              variant: "longRead",
            })}
          </SocialDesktop>
        </Social>

        <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
        <Topic>{provider.topics()}</Topic>
        <Left $stickyStackedHeight={appBarStickyHeight}>
          {provider.shareWidget({ variant: "defaultWithSpeech" })}
          {provider.actionWidget()}
        </Left>

        <HeaderAdSlot>
          {provider.adslot({
            adUnit: "d_banner1",
            sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
          })}
        </HeaderAdSlot>

        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <FooterAdSlot>
              {provider.adslot({
                adUnit: "m_lrec3a",
                sizes: [[300, 250], "fluid"],
                withBackground: true,
                withLabel: true,
              })}
            </FooterAdSlot>
          </GlobalChecker>
        </ClientSideSuspense>
      </InfographicArticleWrapper>
    </InfographicArticleContainer>
  );
};

InfographicArticle.displayName = "InfographicArticle";
